import { randomIntBetween } from '@/lib/random'
import type { ClassTempEntry, QpoItem, TempCheckCategory } from '@/screens/ordering/types'

// Temperature thresholds — update here if SOP changes; never hardcode in components.
// Every band follows "Receiving of Chilled / Frozen Products at Branches"
// (Annex A, FSQ April 2007). Chilled was 4/8 and frozen -12/-5, which rejected
// chilled goods at 9 that the SOP accepts, and accepted thawing frozen goods
// at -6 that it rejects.
//
// `feedback: null` is not "no value yet" — it means the class has no feedback
// band at all, so there is no amber middle ground between accept and reject.
// Ice cream / frozen yoghurt is the one class the SOP writes that way: it
// shares frozen's -12 accept bound but rejects everything above it outright.
// Setting its feedback bound equal to `inRange` would classify identically and
// read as a typo, so the absence is spelled out instead.
export const TEMP_THRESHOLDS = {
  chilled: { inRange: 7, feedback: 10 }, // ≤7 = valid, ≤10 = feedback, >10 = rejected
  frozen: { inRange: -12, feedback: -8 }, // ≤-12 = valid, ≤-8 = feedback, >-8 = rejected
  'ice-cream': { inRange: -12, feedback: null }, // ≤-12 = valid, >-12 = rejected
} as const satisfies Record<TempCheckCategory, { inRange: number; feedback: number | null }>

/**
 * Every class that carries a temperature check, in the order the class card
 * lists them. Derived from `TEMP_THRESHOLDS` so adding a class there is the
 * only edit needed — screens filter this down to the classes actually on a PO.
 */
export const TEMP_CHECK_CATEGORIES: readonly TempCheckCategory[] = Object.keys(
  TEMP_THRESHOLDS,
) as TempCheckCategory[]

/**
 * A fresh class-temperature map with every checked class pending. Built from
 * `TEMP_CHECK_CATEGORIES` so a new class is seeded in both stores and in every
 * screen that re-seeds a session, rather than in five hand-written literals.
 */
export function initialClassTempMap(): Record<TempCheckCategory, ClassTempEntry> {
  return Object.fromEntries(
    TEMP_CHECK_CATEGORIES.map((cat) => [cat, { temp: null, status: 'pending' }]),
  ) as Record<TempCheckCategory, ClassTempEntry>
}

/**
 * A temperature the storekeeper is part-way through typing: optional minus sign,
 * optional digits, at most one decimal point, optional digits. Accepts the
 * in-progress forms a keystroke handler sees ('', '-', '.', '-5.') as well as
 * finished ones ('-12', '4.5'), and rejects anything else.
 *
 * The group is `(?:\.\d*)?` rather than `\.?\d*` on purpose. `\d*\.?\d*` matches
 * the same strings, but its two `\d*` both compete for the same digits, so a
 * rejected input costs the engine one attempt per possible split — quadratic
 * runtime on a long paste (S8786). Here `\d*` can only be followed by a literal
 * `.`, so there is exactly one way to match and no backtracking.
 */
export const PARTIAL_TEMP_INPUT = /^-?\d*(?:\.\d*)?$/

export type TempClassification = 'valid' | 'feedback' | 'rejected'

export function classifyTemp(temp: string, category: TempCheckCategory): TempClassification {
  const value = Number.parseFloat(temp)
  if (Number.isNaN(value)) return 'rejected'
  const t = TEMP_THRESHOLDS[category]
  if (value <= t.inRange) return 'valid'
  // No feedback band means no amber outcome: once the reading is above the
  // accept bound there is nothing left for it to be but rejected.
  if (t.feedback === null) return 'rejected'
  if (value <= t.feedback) return 'feedback'
  return 'rejected'
}

/** Returns true when GR QTY does not match Exch QTY. */
export function isException(item: QpoItem): boolean {
  return item.grQty !== item.exchQty
}

/** Returns the exception type or null when there is no discrepancy. */
export function exceptionType(item: QpoItem): 'shortfall' | 'overstock' | null {
  if (item.grQty < item.exchQty) return 'shortfall'
  if (item.grQty > item.exchQty) return 'overstock'
  return null
}

/** Returns an ISO date string for a random day between tomorrow and `maxDays` from now. */
export function randomFutureDate(maxDays: number): string {
  const ms = randomIntBetween(1, maxDays) * 24 * 60 * 60 * 1000
  return new Date(Date.now() + ms).toISOString().slice(0, 10)
}
